import React, { useCallback, useState } from 'react';
import Box from 'carbon-react/lib/components/box';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { ScheduleListItem } from '@futrli/views/processors/reportScheduling/reportSchedulingList';
import { ListingPageHelpLink } from '@futrli/ui-components/components/listing-page/listing-page-help-link/listing-page-help-link';
import { LISTING_PAGE_TABS_HEIGHT } from '@futrli/ui-components/components/listing-page/listing-page-tabs/listing-page-tabs';

import { VirtualizedCardList } from '../../../../ui/virtualized-card-list/virtualized-card-list';
import { VirtualizedCardListEmptyState } from '../../../../ui/virtualized-card-list/virtualized-card-list-empty-state';
import { TabErrorMessage } from '../error-message/tab-error-message';
import { ListingPageHeader } from '@futrli/ui-components/components/listing-page/listing-page-header/listing-page-header';
import { useNavHeight } from '../../../../hooks/useNavHeight';
import { ReportSchedulingFilters } from './report-scheduling-filters';
import { ScheduleReportRow } from './schedule-report-row';
import { ApproveScheduleDialogContainer } from '../../approve-schedule-dialog/approve-schedule-dialog-container';
import { DenyScheduleDialogContainer } from '../../deny-schedule-dialog/deny-schedule-dialog-container';
import { DeleteScheduleDialogContainer } from '../../delete-schedule-dialog/delete-schedule-dialog-container';
import { useReportSchedules } from './hooks/use-report-schedules';
import { useReportSchedulingFilter } from './hooks/use-report-scheduling-filter';

const CARD_ROW_HEIGHT = 86;

export type ScheduleDialogType = 'delete' | 'approve' | 'deny' | null;

export interface ScheduleDialogState {
  type: ScheduleDialogType;
  item: ScheduleListItem | null;
}

export const ReportSchedulingTab = () => {
  const intl = useIntl();
  const { navHeight } = useNavHeight();

  const { scheduleItems, isLoading, errorLoading, isResourceSharingEnabled } = useReportSchedules();
  const [activeDialog, setActiveDialog] = useState<ScheduleDialogState>({ type: null, item: null });

  const openDialog = useCallback((type: ScheduleDialogType, item: ScheduleListItem) => {
    setActiveDialog({ type, item });
  }, []);

  const closeDialog = useCallback(() => {
    setActiveDialog({ type: null, item: null });
  }, []);

  const {
    filteredData,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    sortValue,
    sortOptions,
    handleSortChange
  } = useReportSchedulingFilter({ scheduleItems });

  const renderRow = useCallback(
    (item: ScheduleListItem) => (
      <ScheduleReportRow
        item={item}
        isResourceSharingEnabled={isResourceSharingEnabled}
        onApprove={i => openDialog('approve', i)}
        onDeny={i => openDialog('deny', i)}
        onDelete={i => openDialog('delete', i)}
      />
    ),
    [openDialog]
  );

  return (
    <Box>
      <ListingPageHeader
        variant="tab-header"
        title={<FormattedMessage id="predict.report_scheduling" defaultMessage="Report scheduling" />}
        helpUrl="https://support.futrli.com/en/articles/14302508-report-scheduling"
      />
      {errorLoading ? (
        <TabErrorMessage />
      ) : (
        <>
          <ReportSchedulingFilters
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            statusFilter={statusFilter}
            onStatusChange={setStatusFilter}
            sortValue={sortValue}
            onSortChange={handleSortChange}
            sortOptions={sortOptions}
            stickyTop={navHeight + LISTING_PAGE_TABS_HEIGHT}
          />
          <Box mt="24px">
            <VirtualizedCardList<ScheduleListItem>
              data={filteredData}
              renderRow={renderRow}
              rowHeight={CARD_ROW_HEIGHT}
              isLoading={isLoading}
              emptyState={
                <VirtualizedCardListEmptyState
                  title={intl.formatMessage({
                    id: 'reporting.no_report_schedules',
                    defaultMessage: 'No report schedules to show'
                  })}
                  action={
                    <ListingPageHelpLink
                      url="https://support.futrli.com/en/articles/14302508-report-scheduling"
                      title={intl.formatMessage({
                        id: 'reporting.empty_report_schedules_help',
                        defaultMessage: 'Learn about report schedules (opens in new tab)'
                      })}
                    />
                  }
                />
              }
            />
          </Box>
        </>
      )}
      {activeDialog.type === 'delete' && activeDialog.item && (
        <DeleteScheduleDialogContainer isOpen onClose={closeDialog} scheduleItem={activeDialog.item} />
      )}
      {activeDialog.type === 'approve' && activeDialog.item && (
        <ApproveScheduleDialogContainer isOpen onClose={closeDialog} item={activeDialog.item} />
      )}
      {activeDialog.type === 'deny' && activeDialog.item && (
        <DenyScheduleDialogContainer isOpen onClose={closeDialog} item={activeDialog.item} />
      )}
    </Box>
  );
};
